import { Component, OnInit } from '@angular/core'
import { ToastrService } from 'ngx-toastr'
import { CommonModule } from '@angular/common'
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormArray,
} from '@angular/forms'
import { PurchaseOrderService } from '../purchaseOrder.service'
import { Router } from '@angular/router'
import { MasterDatabaseDisplayModel } from '../../../shared/master-database-display.model'
import { MatDialog } from '@angular/material/dialog'
import { MasterAddUpdateDialogComponent } from '../../../shared/components/master-add-update-dialog/master-add-update-dialog.component'
import { UnitService } from '../../masters/unit.master/unit.service'
import { MasterCommonServices } from '../../quotation/master.common.service'
import { VendorMemberModel } from '../../member-vendor/member-vendor.model'
import { UserModel } from '../../users/user_model'

@Component({
  selector: 'app-purchaseOrder',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './addPurchaseOrder.component.html',
  styleUrls: ['./addPurchaseOrder.component.css'],
})
export class AddPurchaseOrderComponent implements OnInit {
  title: string = 'Add New Purchase Order'
  isLoading: boolean = false
  submitted: boolean = false
  isToastrShown: boolean = false
  purchaseOrderForm: FormGroup = new FormGroup({})
  currentPriority: number = 0
  vendorMembersList: VendorMemberModel[] = []
  mode: string = 'add'
  purchaseOrderData: any
  permissions: string[] = []
  purchaseOrderUsers: UserModel[] = []

  unitColumns: MasterDatabaseDisplayModel[] = [
    {
      key: 'unitName',
      displayName: 'Unit Name',
      hiddenDataTable: false,
      hiddenDialog: false,
      type: 'text',
    },
    {
      key: 'id',
      displayName: 'Id',
      hiddenDataTable: true,
      hiddenDialog: true,
      type: 'text',
    },
  ]

  constructor(
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    public masterCommonServices: MasterCommonServices,
    private purchaseOrderService: PurchaseOrderService,
    private unitService: UnitService,
    private router: Router,
    private dialog: MatDialog,
  ) {
    this.initializeForm()
    const navigation = this.router.getCurrentNavigation()
    if (navigation?.extras.state) {
      const params = navigation.extras.state

      this.mode = params['mode'] || 'add'
      if (this.mode !== 'add') {
        this.purchaseOrderData = {
          purchaseOrderFields: JSON.parse(params['purchaseOrderFields']),
          purchaseOrderItems: JSON.parse(params['purchaseOrderItems']),
          purchaseOrderTerms: JSON.parse(params['purchaseOrderTerms']),
        }
        this.populateForm(this.purchaseOrderData)
        this.title = 'Update Purchase Order'
      }
    }
  }
  ngOnInit(): void {
    this.setUpMasters()
  }

  async setUpMasters(): Promise<void> {
    if (!this.masterCommonServices.isDataLoaded) {
      await this.masterCommonServices.getAllMasters()
    }
    this.purchaseOrderUsers = this.masterCommonServices.usersList.filter(
      (user) => user.userPermissions?.includes('PurchaseOrdersAccess'),
    )
  }
  onToggleRemoveGst(): void {
    this.purchaseOrderForm.markAsDirty()
    this.calculateGrandTotal()
  }

  autoResize(event: any) {
    event.target.style.height = 'auto'
    event.target.style.height = event.target.scrollHeight + 'px'
  }

  openAddUpdateUnitDialog(): void {
    const mode = 'add'
    const fields = this.unitColumns
    const dialogRef = this.dialog.open(MasterAddUpdateDialogComponent, {
      data: { item: {}, mode, fields },
    })

    dialogRef.afterClosed().subscribe((result) => {
      this.addUnit(result)
    })
  }

  addUnit(unit: any): void {
    this.unitService.addUnit(unit).subscribe({
      next: () => {
        this.toastr.success('Unit Added Successfully!', 'Success')
        this.unitService.getAllUnits().subscribe({
          next: (data: any) => {
            this.masterCommonServices.unitsList = data
          },
          error: (error: any) => {
            this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
          },
        })
      },
      error: (error: any) => {
        this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
      },
    })
  }

  populateForm(data: any): void {
    this.purchaseOrderForm
      .get('purchaseOrderFields')
      ?.get('purchaseOrderAttentionId')
      ?.setValue(null)
    this.purchaseOrderForm.patchValue({
      purchaseOrderFields: {
        ...data.purchaseOrderFields,
        purchaseOrderDate: data.purchaseOrderFields.purchaseOrderDate.split(
          'T',
        )[0],
      },
      purchaseOrderTerms: data.purchaseOrderTerms,
    })
    this.onCompanyChange()
    this.purchaseOrderForm
      .get('purchaseOrderFields')
      ?.get('purchaseOrderAttentionId')
      ?.setValue(data.purchaseOrderFields.purchaseOrderAttentionId.toString())
    if (data.purchaseOrderItems?.length > 0) {
      const itemsFormArray = this.purchaseOrderForm.get(
        'purchaseOrderItems',
      ) as FormArray
      itemsFormArray?.clear()

      data.purchaseOrderItems.forEach((item: any) => {
        const itemGroup = this.formBuilder.group({
          id: [item.id],
          srNo: [item.srNo],
          purchaseOrderId: data.purchaseOrderFields.id,
          description1: [item.description1, Validators.required],
          description2: [item.description2, Validators.required],
          quantity: [item.quantity, Validators.required],
          unit: [item.unit, Validators.required],
          hsn: [item.hsn, Validators.required],
          inwardQuantity: [item.inwardQuantity],
          isDeleted: [item.isDeleted],
          unitPrice: [item.unitPrice, Validators.required],
          totalPrice: [{ value: item.totalPrice, disabled: true }],
        })
        itemsFormArray?.push(itemGroup)
      })
    }
    if (data.purchaseOrderTerms.deliveryTime !== null) {
      const dateFormatted = data.purchaseOrderTerms.deliveryTime.split('T')[0]
      this.purchaseOrderForm
        .get('purchaseOrderTerms')
        ?.get('deliveryTime')
        ?.setValue(dateFormatted)
    }
  }

  initializeForm(): void {
    this.purchaseOrderForm = this.formBuilder.group({
      purchaseOrderFields: this.createPurchaseOrderFields(),
      purchaseOrderItems: this.formBuilder.array([]),
      purchaseOrderTerms: this.createPurchaseOrderTerms(),
    })
  }

  createPurchaseOrderFields(): FormGroup {
    return this.formBuilder.group({
      purchaseOrderMadeById: [null, Validators.required],
      purchaseOrderAssignedToId: [null, Validators.required],
      id: [{ value: null, disabled: true }],
      purchaseOrderId: [{ value: null, disabled: true }],
      purchaseOrderDate: [
        new Date().toISOString().substring(0, 10),
        Validators.required,
      ],
      purchaseOrderVendorId: [null, Validators.required],
      purchaseOrderAttentionId: [null, Validators.required],
      netTotal: [{ value: null, disabled: true }],
      discountType: [null],
      discount: [null],
      gstAmount: [{ value: null, disabled: true }],
      otherCharges: [null],
      grandTotal: [{ value: null, disabled: true }],
      isGstRemoved: [false],
      isRoundOff: [false],
    })
  }

  createPurchaseOrderTerms(): FormGroup {
    return this.formBuilder.group({
      id: [null],
      purchaseOrderId: [null],
      deliveryNameId: [null],
      currencyId: [null],
      deliveryTime: [null],
      countryofOriginId: [null],
      paymentId: [null],
      mtcTypeId: [null],
      validityId: [null],
      packingTypeId: [null],
    })
  }

  showToastrError(message: string) {
    if (!this.isToastrShown) {
      this.isToastrShown = true
      this.toastr.error(message, 'Error').onHidden.subscribe(() => {
        this.isToastrShown = false
      })
    }
  }

  onPriorityChange(event: Event) {
    this.purchaseOrderForm.markAsDirty()
    this.currentPriority = +(event.target as HTMLInputElement).value
  }

  onPurchaseOrderItemFieldChanged(index: number): void {
    this.purchaseOrderForm.markAsDirty()
    const itemGroup = this.purchaseOrderItems.at(index) as FormGroup
    const qty = itemGroup.get('quantity')?.value || 0
    let cost = itemGroup.get('cost')?.value || 0
    const margin = itemGroup.get('margin')?.value || 0
    const packagingCharges = itemGroup.get('packagingCharges')?.value || 0
    let unitPrice = cost
    unitPrice += (margin / 100) * unitPrice
    unitPrice += (packagingCharges / 100) * unitPrice
    const totalPrice = qty * unitPrice
    itemGroup.get('unitPrice')?.setValue(unitPrice.toFixed(2))
    itemGroup.get('totalPrice')?.setValue(totalPrice.toFixed(2))
    this.calculateNetTotal()
    this.calculateGrandTotal()
  }

  onUnitPriceChanged(index: number): void {
    this.purchaseOrderForm.markAsDirty()
    const itemGroup = this.purchaseOrderItems.at(index) as FormGroup
    const unitPrice = itemGroup.get('unitPrice')?.value || 0
    const qty = itemGroup.get('quantity')?.value || 0
    const totalPrice = qty * unitPrice
    itemGroup.get('totalPrice')?.setValue(totalPrice.toFixed(2))
    this.calculateNetTotal()
    this.calculateGrandTotal()
  }

  calculateNetTotal(): void {
    let netTotal: number = 0

    this.purchaseOrderItems.controls.forEach((itemGroup) => {
      const isDeleted = itemGroup.get('isDeleted')?.value
      if (!isDeleted) {
        const totalPrice =
          Number.parseFloat(itemGroup.get('totalPrice')?.value) || 0
        netTotal += totalPrice
      }
    })
    this.purchaseOrderForm
      .get('purchaseOrderFields')
      ?.get('netTotal')
      ?.setValue(netTotal)
  }

  calculateGrandTotal(): void {
    const purchaseOrderFieldGroup = this.purchaseOrderForm.get(
      'purchaseOrderFields',
    )
    const netTotal = purchaseOrderFieldGroup?.get('netTotal')?.value || 0
    const discountType =
      purchaseOrderFieldGroup?.get('discountType')?.value || 0
    const discountValue = purchaseOrderFieldGroup?.get('discount')?.value || 0
    const pAndf = purchaseOrderFieldGroup?.get('otherCharges')?.value || 0

    let discountAmount = 0
    if (discountType === 'percent') {
      discountAmount = (discountValue / 100) * netTotal
    } else if (discountType === 'value') {
      discountAmount = discountValue
    }

    const discountedAmount = netTotal - discountAmount

    let gstAmount = 0
    const isGstRemoved = this.purchaseOrderForm
      .get('purchaseOrderFields')
      ?.get('isGstRemoved')
      ?.value
    if (!isGstRemoved) {
      const gstPercent = 18 / 100
      const discountGst = discountedAmount * gstPercent
      const pAndfGst = pAndf * gstPercent
      gstAmount = discountGst + pAndfGst
    }

    this.purchaseOrderForm
      .get('purchaseOrderFields')
      ?.get('gstAmount')
      ?.setValue(gstAmount.toFixed(2))
    let grandTotal = discountedAmount + pAndf + gstAmount
    const isRoundOff = this.purchaseOrderForm.get('purchaseOrderFields')
      ?.get('isRoundOff')
      ?.value
    if (isRoundOff) {
      grandTotal = Math.round(grandTotal)
    }
     this.purchaseOrderForm
      .get('purchaseOrderFields')
      ?.get('grandTotal')
      ?.setValue(grandTotal.toFixed(2))
  }

  toggleRoundOff(event: Event): void {
    this.purchaseOrderForm.markAsDirty()
    const isChecked = (event.target as HTMLInputElement).checked
    const grandTotalControl = this.purchaseOrderForm
      .get('purchaseOrderFields')
      ?.get('grandTotal')

    if (isChecked) {
      const roundedValue = Math.round(grandTotalControl?.value || 0)
      grandTotalControl?.setValue(roundedValue)
    } else {
      this.calculateGrandTotal()
    }
  }

  get purchaseOrderItems(): FormArray {
    return this.purchaseOrderForm.get('purchaseOrderItems') as FormArray
  }

  addRow() {
    this.purchaseOrderForm.markAsDirty()
    const itemGroup = this.formBuilder.group({
      id: [null],
      srNo: [null],
      description1: [null, Validators.required],
      description2: [null, Validators.required],
      quantity: [null, Validators.required],
      unit: [null, Validators.required],
      hsn: [null, Validators.required],
      inwardQuantity: [0, Validators.required],
      isDeleted: [false],
      unitPrice: [null, Validators.required],
      totalPrice: [{ value: null, disabled: true }],
    })
    this.purchaseOrderItems.push(itemGroup)
  }

  removeRow(index: number): void {
    this.purchaseOrderForm.markAsDirty()
    const formArray = this.purchaseOrderItems as FormArray
    const formGroup = formArray.at(index) as FormGroup
    formGroup.patchValue({ isDeleted: true })
    Object.keys(formGroup.controls).forEach((key) => {
      formGroup.get(key)?.clearValidators()
      formGroup.get(key)?.updateValueAndValidity()
    })
    this.calculateNetTotal()
    this.calculateGrandTotal()
    
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.resizeAllTextareas())
  }

  resizeAllTextareas(): void {
    const textareas = document.querySelectorAll('textarea.auto-resize')
    textareas.forEach((textarea: any) => {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    })
  }

  hasVisibleRows(): boolean {
    return this.purchaseOrderItems.controls.some(
      (item) => !item.get('isDeleted')?.value,
    )
  }

  submit() {
    this.submitted = true

    if (this.purchaseOrderForm.invalid && this.hasVisibleRows()) {
      
      this.toastr.error('Please fill all the required fields', 'Error')
      return
    }

    if (this.hasVisibleRows() == false) {
      this.toastr.error('Please add at least one item', 'Error')
      return
    }
    
    const purchaseOrderData = this.purchaseOrderForm.getRawValue()
    

    const processedData = this.processFormData(purchaseOrderData)
    const isGstRemoved = this.purchaseOrderForm
      .get('purchaseOrderFields')
      ?.get('isGstRemoved')
      ?.value
    if(isGstRemoved) {
      processedData.gstPercent = 0
    }else{
      processedData.gstPercent = 18
    }
    if (this.mode === 'add' && this.purchaseOrderForm.get('purchaseOrderFields')?.get('purchaseOrderId')?.value === null) {
      

      this.purchaseOrderService.addPurchaseOrder(processedData).subscribe({
        next: (data) => {
          if(data.id){
            this.purchaseOrderService.getPurchaseOrderById(data.id).subscribe({
            next: (po) => {
              this.populateForm(po)
            },
            error: (error) => {
              this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
            },
          })
          }
          this.purchaseOrderForm.markAsPristine();
          this.title = 'Update Purchase Order'
          this.toastr.success('Purchase Order Added Successfully!', 'Success')
        },
        error: (error: any) => {
          this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
        },
      })
    } else {
      this.purchaseOrderService.updatePurchaseOrder(processedData).subscribe({
        next: (data) => {
          if(data.id){
            this.purchaseOrderService.getPurchaseOrderById(data.id).subscribe({
            next: (po) => {
              this.populateForm(po)
            },
            error: (error) => {
              this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
            },
          })
          }
          this.purchaseOrderForm.markAsPristine();
          this.toastr.success('Purchase Order Updated Successfully!', 'Success')
        },
        error: (error: any) => {
          this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
        },
      })
    }
  }

  processFormData(data: any): any {
    const dateTimeNow = new Date(
      new Date().getTime() - new Date().getTimezoneOffset() * 60000,
    ).toISOString()
    if (this.mode === 'add') {
      data.purchaseOrderFields.addedOn = dateTimeNow
      data.purchaseOrderTerms.addedOn = dateTimeNow
    } else {
      data.purchaseOrderFields.addedOn = this.purchaseOrderData.purchaseOrderFields.addedOn
      data.purchaseOrderTerms.addedOn = this.purchaseOrderData.purchaseOrderTerms.addedOn
    }
    data.purchaseOrderFields.modifiedOn = dateTimeNow
    data.purchaseOrderTerms.modifiedOn = dateTimeNow
    if (Array.isArray(data.purchaseOrderItems)) {
      data.purchaseOrderItems = data.purchaseOrderItems
        .filter((item: any) => this.mode === 'update' || !item.isDeleted) // Remove deleted items
        .map((item: any, index: number) => {
          item.srNo = index + 1
          item.addedOn = dateTimeNow
          item.modifiedOn = dateTimeNow
          this.processFields(item)
          return item
        })
    }
    Object.keys(data.purchaseOrderTerms).forEach((key) => {
      const value = data.purchaseOrderTerms[key]
      if (value === 'null' || value === '') {
        data.purchaseOrderTerms[key] = null
      }
    })
    return data
  }

  processFields(fields: any): void {
    for (const key in fields) {
      if (fields.hasOwnProperty(key)) {
        const value = fields[key]

        if (value === null || value === undefined) {
          if (this.isNumericField(key)) {
            fields[key] = 0
          }
        }
      }
    }
  }

  isNumericField(key: string): boolean {
    const numericFields = [
      'discount',
      'otherCharges',
      'unitPrice',
      'totalPrice',
      'grandTotal',
      'netTotal',
      'gstAmount',
      'quantity',
      'cost',
      'margin',
      'packagingCharges',
    ]
    return numericFields.includes(key)
  }

  getSerialNumber(index: number): number {
    let count = 1
    for (let i = 0; i < index; i++) {
      if (!this.purchaseOrderItems.at(i).get('isDeleted')?.value) {
        count++
      }
    }
    return count
  }

  onCompanyChange() {
    this.purchaseOrderForm.markAsDirty()
    const selectedCompanyId = this.purchaseOrderForm
      .get('purchaseOrderFields')
      ?.get('purchaseOrderVendorId')
      ?.value.toString()
    this.vendorMembersList = this.masterCommonServices.vendorMemberList.filter(
      (member) => member.vendorId.toString() === selectedCompanyId,
    )
    if (this.vendorMembersList.length > 0) {
      this.purchaseOrderForm
        .get('purchaseOrderFields')
        ?.get('purchaseOrderAttentionId')
        ?.setValue(this.vendorMembersList[0].id)
    }
  }
}
